import React, { useRef, useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { Dimmer, Loader, Message, Button } from 'semantic-ui-react'
import SuperCutoffWizard from '../../../components/core/supercutoff/SuperCutoffWizard'
import Can from '../../../abilities/Can'
import {
  startCrToggle,
  startBulkApprovePreview,
  startBulkApproveExecute,
  startRevenueResetPreview,
  startRevenueResetExecute,
  startGetOpenCrs,
  startCrClean,
  startSetSupercutoff,
  startGetProgress,
  startGetErrors,
  startRetryErrors,
  startGetSupercutoffState,
  startCompleteErrors,
} from '../../../store/actions/core/supercutoff/supercutoff'
import { startGetCutoffPM } from '../../../store/actions/core/administration/cutoff'
import useSupercutoffWizard from './useSupercutoffWizard'
import './SuperCutoffContainer.css'

/**
 * Container principale del wizard SuperCutoff.
 * Idrata il wizard dal payload di GET /supercutoff/state (single source of truth lato BE)
 * e delega l'orchestrazione a useSupercutoffWizard.
 *
 * Props (da Redux — loading states):
 *   loadingCrToggle            {boolean}  Loading toggle CR
 *   loadingBulkApprovePreview  {boolean}  Loading preview bulk approve
 *   loadingBulkApproveExecute  {boolean}  Loading esecuzione bulk approve
 *   loadingRevenueResetPreview {boolean}  Loading preview reset ricavi
 *   loadingRevenueResetExecute {boolean}  Loading esecuzione reset ricavi
 *   loadingOpenCrs             {boolean}  Loading caricamento CR aperte
 *   loadingCrClean             {boolean}  Loading pulizia CR
 *   loadingSetSupercutoff      {boolean}  Loading impostazione supercutoff
 *   loadingProgress            {boolean}  Loading aggiornamento progresso
 *   loadingErrors              {boolean}  Loading caricamento errori
 *   loadingRetryErrors         {boolean}  Loading retry errori
 *   loadingCompleteErrors      {boolean}  Loading completamento step errori
 *   loadingWizardState         {boolean}  Loading hydration dello state wizard
 *
 * Props (da Redux — dati):
 *   bulkApprovePreview   {Object}  Dati preview bulk approve
 *   bulkApproveResult    {Object}  Risultato esecuzione bulk approve
 *   revenueResetPreview  {Object}  Dati preview reset ricavi
 *   revenueResetResult   {Object}  Risultato esecuzione reset ricavi
 *   openCrs              {Array}   CR aperte
 *   crCleanResult        {Object}  Risultato pulizia CR
 *   supercutoffResult    {Object}  Risultato impostazione supercutoff
 *   progress             {Object}  Stato avanzamento chiusura
 *   errors               {Array}   Errori da gestire
 *   retryResult          {Object}  Risultato retry errori
 *   wizardState          {Object}  Snapshot del wizard server-side; null se non caricato o run non attiva
 *   wizardStateError     {boolean} True se l'ultimo fetch dello state è fallito (pilotato dal reducer)
 *   cutoffPm             {Array}   Lista cutoff PM (GET /pm/cutoffs/), usata per derivare il "mese successivo"
 *                                  vincolante per gli step Bulk approve e Set SuperCutoff PM
 *   loadingCutoffPm      {boolean} Loading della chiamata cutoff PM
 *
 * Props (da Redux — dispatchers):
 *   Nota: i thunk preview usano un nome con prefisso `dispatch...` per evitare
 *   collisioni con le chiavi omonime di mapStateToProps (es. `bulkApprovePreview`
 *   è anche un dato dello store). In react-redux, mergeProps unisce in ordine
 *   `{ ...stateProps, ...dispatchProps }`, quindi i dispatchProps sovrascrivono
 *   gli stateProps con la stessa chiave: senza il rename, `props.bulkApprovePreview`
 *   sarebbe la funzione thunk al posto del dato (e l'execute partirebbe senza preview).
 *
 *   crToggle                       {Function}  Thunk toggle CR
 *   dispatchBulkApprovePreview     {Function}  Thunk preview bulk approve
 *   bulkApproveExecute             {Function}  Thunk esecuzione bulk approve
 *   dispatchRevenueResetPreview    {Function}  Thunk preview reset ricavi
 *   revenueResetExecute            {Function}  Thunk esecuzione reset ricavi
 *   getOpenCrs           {Function}  Thunk caricamento CR aperte
 *   crClean              {Function}  Thunk pulizia CR
 *   setSupercutoff       {Function}  Thunk impostazione supercutoff
 *   getProgress          {Function}  Thunk aggiornamento progresso
 *   getErrors            {Function}  Thunk caricamento errori
 *   retryErrors          {Function}  Thunk retry errori
 *   completeErrors       {Function}  Thunk completamento step errori (POST /errors/complete)
 *   getSupercutoffState  {Function}  Thunk hydration dello state (GET /supercutoff/state)
 *   getCutoffPm          {Function}  Thunk lista cutoff PM (GET /pm/cutoffs/)
 */
const SuperCutoffContainer = (props) => {
  const {
    // Dati Redux
    loadingCrToggle,
    loadingBulkApprovePreview,
    loadingBulkApproveExecute,
    loadingRevenueResetPreview,
    loadingRevenueResetExecute,
    loadingOpenCrs,
    loadingCrClean,
    loadingSetSupercutoff,
    loadingProgress,
    loadingErrors,
    loadingRetryErrors,
    loadingCompleteErrors,
    loadingWizardState,
    bulkApprovePreview,
    bulkApproveResult,
    revenueResetPreview,
    revenueResetResult,
    openCrs,
    crCleanResult,
    supercutoffResult,
    progress,
    errors,
    retryResult,
    wizardState,
    wizardStateError,
    cutoffPm,
    loadingCutoffPm,
    // Thunk dispatchers
    crToggle,
    dispatchBulkApprovePreview,
    bulkApproveExecute,
    dispatchRevenueResetPreview,
    revenueResetExecute,
    getOpenCrs,
    crClean,
    setSupercutoff,
    getProgress,
    getErrors,
    retryErrors,
    completeErrors,
    getSupercutoffState,
    getCutoffPm,
  } = props

  const reduxData = {
    loadingCrToggle,
    loadingBulkApprovePreview,
    loadingBulkApproveExecute,
    loadingRevenueResetPreview,
    loadingRevenueResetExecute,
    loadingOpenCrs,
    loadingCrClean,
    loadingSetSupercutoff,
    loadingProgress,
    loadingErrors,
    loadingRetryErrors,
    loadingCompleteErrors,
    bulkApprovePreview,
    bulkApproveResult,
    revenueResetPreview,
    revenueResetResult,
    openCrs,
    crCleanResult,
    supercutoffResult,
    progress,
    errors,
    retryResult,
    wizardState,
    cutoffPm,
    loadingCutoffPm,
  }

  const dispatchers = {
    crToggle,
    bulkApprovePreview: dispatchBulkApprovePreview,
    bulkApproveExecute,
    revenueResetPreview: dispatchRevenueResetPreview,
    revenueResetExecute,
    getOpenCrs,
    crClean,
    setSupercutoff,
    getProgress,
    getErrors,
    retryErrors,
    completeErrors,
    getSupercutoffState,
    getCutoffPm,
  }

  const {
    steps,
    activeStepId,
    activeStepTitle,
    stepData,
    handlers,
    loadings,
    onSelectStep,
  } = useSupercutoffWizard(reduxData, dispatchers)

  // Hydration dello state al mount: il payload contiene la run attiva (se esiste)
  // e cr_available, sostituendo la precedente chiamata GET /cr-status (deprecata).
  useEffect(() => {
    getSupercutoffState()
  }, [getSupercutoffState])

  // Caricamento della lista cutoff PM al mount: usata da Bulk approve e Set SuperCutoff PM
  // per derivare il "mese successivo" vincolante (vedi useSupercutoffWizard.nextPeriod).
  // Il fetch e indipendente da getSupercutoffState e si sblocca anche se la run e gia attiva,
  // perche cutoff_pm e necessario in ogni caso per costruire il payload dei due step.
  useEffect(() => {
    getCutoffPm()
  }, [getCutoffPm])

  // Sopprime l'annuncio aria-live al primo render: i lettori di schermo
  // non devono annunciare il titolo del primo step al caricamento della pagina,
  // ma solo in risposta a una transizione esplicita dell'utente.
  const isFirstRender = useRef(true)
  const [liveAnnouncement, setLiveAnnouncement] = useState('')

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    setLiveAnnouncement(activeStepTitle)
  }, [activeStepTitle])

  // Hydration iniziale: nessun wizardState ancora caricato e fetch in corso.
  // Mostriamo solo il loader, non lo step 1, per evitare flash dello stato pulito
  // quando in realtà esiste una run attiva.
  const isInitialHydrating = wizardState === null && loadingWizardState

  // Errore di hydration: il payload non è disponibile e abbiamo registrato un errore.
  // Mostriamo Message + retry, non lo step pulito che maschererebbe lo stato reale.
  const isHydrationError = wizardState === null && wizardStateError && !loadingWizardState

  return (
    <Can I='supercutoff:Read' a='supercutoff'>
      {() => (
        <>
          {/*
            Annuncio accessibile del cambio step per screen reader.
            aria-live="polite" garantisce che il titolo venga letto
            al termine dell'azione corrente, senza interrompere.
            La classe sr-only nasconde visivamente l'elemento.
          */}
          <span
            className="sr-only"
            aria-live="polite"
            aria-atomic="true"
          >
            {liveAnnouncement}
          </span>

          {isInitialHydrating && (
            <div className="supercutoff-hydration-wrapper">
              <Dimmer active inverted>
                <Loader indeterminate inverted content="Loading SuperCutoff state" size="medium" />
              </Dimmer>
            </div>
          )}

          {isHydrationError && (
            <Message negative className="supercutoff-hydration-error">
              <Message.Header>Unable to load SuperCutoff state</Message.Header>
              <p>
                The current wizard state could not be retrieved from the server.
                Retry to attempt loading again.
              </p>
              <Button
                primary
                onClick={getSupercutoffState}
                disabled={loadingWizardState}
                loading={loadingWizardState}
              >
                Retry
              </Button>
            </Message>
          )}

          {!isInitialHydrating && !isHydrationError && (
            <SuperCutoffWizard
              steps={steps}
              activeStepId={activeStepId}
              onSelectStep={onSelectStep}
              stepData={stepData}
              handlers={handlers}
              loadings={loadings}
            />
          )}
        </>
      )}
    </Can>
  )
}

const mapStateToProps = (state) => ({
  // Loading states
  loadingCrToggle:            state.supercutoffReducer.loadingCrToggle,
  loadingBulkApprovePreview:  state.supercutoffReducer.loadingBulkApprovePreview,
  loadingBulkApproveExecute:  state.supercutoffReducer.loadingBulkApproveExecute,
  loadingRevenueResetPreview: state.supercutoffReducer.loadingRevenueResetPreview,
  loadingRevenueResetExecute: state.supercutoffReducer.loadingRevenueResetExecute,
  loadingOpenCrs:             state.supercutoffReducer.loadingOpenCrs,
  loadingCrClean:             state.supercutoffReducer.loadingCrClean,
  loadingSetSupercutoff:      state.supercutoffReducer.loadingSetSupercutoff,
  loadingProgress:            state.supercutoffReducer.loadingProgress,
  loadingErrors:              state.supercutoffReducer.loadingErrors,
  loadingRetryErrors:         state.supercutoffReducer.loadingRetryErrors,
  loadingCompleteErrors:      state.supercutoffReducer.loadingCompleteErrors,
  loadingWizardState:         state.supercutoffReducer.loadingWizardState,
  // Data
  bulkApprovePreview:  state.supercutoffReducer.bulkApprovePreview,
  bulkApproveResult:   state.supercutoffReducer.bulkApproveResult,
  revenueResetPreview: state.supercutoffReducer.revenueResetPreview,
  revenueResetResult:  state.supercutoffReducer.revenueResetResult,
  openCrs:             state.supercutoffReducer.openCrs,
  crCleanResult:       state.supercutoffReducer.crCleanResult,
  supercutoffResult:   state.supercutoffReducer.supercutoffResult,
  progress:            state.supercutoffReducer.progress,
  errors:              state.supercutoffReducer.errors,
  retryResult:         state.supercutoffReducer.retryResult,
  wizardState:         state.supercutoffReducer.wizardState,
  wizardStateError:    state.supercutoffReducer.wizardStateError,
  cutoffPm:            state.cutoffReducer.cutoff_pm,
  loadingCutoffPm:     state.cutoffReducer.loading_pm,
})

const mapDispatchToProps = {
  crToggle:                    startCrToggle,
  dispatchBulkApprovePreview:  startBulkApprovePreview,
  bulkApproveExecute:          startBulkApproveExecute,
  dispatchRevenueResetPreview: startRevenueResetPreview,
  revenueResetExecute:         startRevenueResetExecute,
  getOpenCrs:                  startGetOpenCrs,
  crClean:                     startCrClean,
  setSupercutoff:              startSetSupercutoff,
  getProgress:                 startGetProgress,
  getErrors:                   startGetErrors,
  retryErrors:                 startRetryErrors,
  completeErrors:              startCompleteErrors,
  getSupercutoffState:         startGetSupercutoffState,
  getCutoffPm:                 startGetCutoffPM,
}

export default connect(mapStateToProps, mapDispatchToProps)(SuperCutoffContainer)
